import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from '../Services/FAQAccordion';
import Seo from '../Seo';

const SolutionFulltime = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



  const faqs = [
    {
      q: "How is your full-time executive search different?",
      a: "Our full-time search model is built around long-term success, not just quick role closure. We begin with deep discovery of leadership expectations, team dynamics, growth plans, and role impact before entering market outreach. Candidate evaluation includes technical, behavioral, and organizational fit dimensions to reduce mismatch risk. We also calibrate shortlist quality with stakeholder feedback at each stage to improve decision confidence. This approach consistently improves retention and accelerates time-to-impact after onboarding."
    },
    {
      q: "What is your typical placement timeframe?",
      a: "Timelines vary by seniority, specialization, and market availability, but our process is designed for disciplined speed with quality controls. We align role criteria and evaluation rubrics early so market search and candidate screening move efficiently. For many roles, initial shortlists can be presented within the first engagement window with clear fit rationale. Executive and niche technical positions may require longer sourcing cycles with deeper assessment stages. Throughout the process, we maintain milestone visibility so hiring leaders can plan confidently."
    },
    {
      q: "How do you ensure candidate quality?",
      a: "Candidate quality is managed through multi-layered evaluation that goes beyond resume keyword matching. We combine domain-specific screening, structured interviews, and competency-based assessment to validate role readiness. Cultural alignment and leadership compatibility are assessed in parallel so hires succeed in team context, not just in technical scope. We also verify communication effectiveness and problem-solving approach for high-impact functions. This rigorous model improves hiring accuracy and supports stronger long-term retention."
    },
    {
      q: "Do you support niche technical and leadership roles together?",
      a: "Yes. Our search model supports both senior leadership roles and specialized technical positions that require deep market mapping. We calibrate each search against role impact, team maturity, and organizational transformation goals. This enables balanced hiring across strategic leadership, architecture, delivery, and domain-specific capabilities. Our process also helps align compensation and candidate value proposition for competitive markets. As a result, clients can build cohesive teams rather than isolated hires."
    },
    {
      q: "What happens after a candidate joins?",
      a: "Post-join support is a critical part of long-term hiring success, especially for high-impact roles. We maintain structured check-ins with stakeholders to assess onboarding progress, expectation alignment, and early performance signals. If risks are identified, we coordinate corrective support quickly to stabilize integration and team dynamics. This reduces early attrition and helps new hires deliver value sooner. Our objective is sustained role success, not just placement closure."
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-4 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Full-Time Placements"
        description="Libsys full-time placements — rigorous candidate screening, cultural fit assessment, and post-join support for permanent hires in executive and niche roles."
        path="/solutions/fulltime"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-6">
        <div className="rounded-[2.5rem] border border-[#236086]/10 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-red-tagline mb-5">
                Executive & Permanent Talent Acquisition
              </span>
              <h1 className="text-h1 text-primary mb-6">
                Building the Core of Your Enterprise with <span className="text-secondary">Elite Talent</span>
              </h1>
              <p className="text-slate-600 text-body leading-[1.8] font-medium mb-8 max-w-2xl">
                A single strategic hire has the power to redefine the trajectory of your entire organization. Libsys specializes in identifying, vetting, and securing high-impact permanent talent for critical leadership and niche technical roles. We go beyond simple keyword matching to align candidate vision, culture, and long-term potential with your enterprise goals. Our full-time hiring model combines deep role calibration with rigorous behavioral assessments and post-join integration support, ensuring your core team is built for sustained excellence.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10">
                {[
                  'Executive and niche talent search',
                  'Technical & behavioral assessments',
                  'Culture & leadership fit mapping',
                  'Retention-focused onboarding',
                  'Impact-driven sourcing models',
                  'Continuous success monitoring'
                ].map((item) => (
                  <li key={item} className="text-slate-700 text-small font-bold leading-relaxed flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+16307991556"
                  className="inline-flex items-center gap-3 px-10 py-5 bg-primary hover:bg-secondary text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-xl active:scale-95 group"
                >
                  Start Executive Search
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7 lg:-mt-12">
              <div className="rounded-[2.5rem] border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group overflow-hidden">
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
                 <img
                    src="/Fulltime1.png"
                    alt="Full-time placement simulation"
                    className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000"
                    onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                  />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ EXECUTIVE VALUE SECTION ═══════════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-h2 text-primary mb-6">Securing the Architects of Your Future</h2>
            <p className="text-slate-600 font-medium leading-relaxed">
              Our permanent placement model focuses on the intersection of technical prowess and organizational compatibility to ensure long-term stability.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Strategic Discovery", desc: "We conduct deep-dive sessions to understand your team dynamics and growth plans before we even start the search." },
              { title: "Precision Vetting", desc: "Every executive candidate undergoes multi-layered evaluations for both technical depth and leadership maturity." },
              { title: "Longevity Focus", desc: "We measure success by retention. Our placements are designed to integrate and thrive within your unique culture." }
            ].map((adv, i) => (
              <div key={i} className="p-10 rounded-[2rem] bg-[#e2edf5] border border-[#236086]/10 hover:shadow-xl transition-all">
                <h4 className="text-xl font-black text-primary mb-4">{adv.title}</h4>
                <p className="text-slate-600 text-body leading-relaxed font-medium">{adv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CORE VALUE ══════════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-red-tagline block mb-4">Leadership & Stability</span>
              <h2 className="text-h2 text-primary mb-6">Permanent Talent Built for Long-Term Impact</h2>
              <p className="text-slate-600 text-small mb-8 leading-[1.8] font-medium">
                Our permanent hiring methodology is centered on the belief that long-term success is the only valid metric. We don't just fill open requisitions; we help you build the foundation of your future leadership. By conducting rigorous multi-layered assessments—covering technical depth, behavioral traits, and organizational alignment—we ensure that every candidate we present is capable of delivering immediate value while maintaining long-term commitment. Libsys stays engaged through the critical integration period to ensure your new hires are positioned for sustained success from day one.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 mb-10">
                {[
                  { title: "Market Mapping", desc: "Exhaustive identification of passive talent across global tech hubs." },
                  { title: "Fit Assessment", desc: "Rigorous behavioral and leadership evaluation for long-term sync." },
                  { title: "Onboarding Sync", desc: "Structured support to stabilize your new hires during integration." },
                  { title: "Elite Sourcing", desc: "Securing the top 1% of talent for high-impact enterprise roles." }
                ].map((item, i) => (
                  <div key={i} className="space-y-2.5">
                    <div className="text-secondary flex items-center gap-2">
                      <span className="font-black text-primary text-tiny uppercase tracking-widest">{item.title}</span>
                    </div>
                    <p className="text-slate-600 text-subtext leading-relaxed font-semibold">{item.desc}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-4">
                {[
                  'Bilingual and multi-cultural talent mapping (USA & India)',
                  'Customized evaluation rubrics aligned with your internal values',
                  'Post-join check-ins to monitor retention and performance metrics'
                ].map((item) => (
                  <li key={item} className="text-slate-800 text-subtext font-bold leading-relaxed flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
               <div className="rounded-[2.5rem] overflow-hidden border border-[#236086]/10 shadow-2xl bg-white p-2">
                 <img
                  src="/Fulltime2.png"
                  alt="Global full-time talent solutions"
                  className="w-full h-auto aspect-square lg:aspect-[4/3] object-cover rounded-[2.2rem]"
                  onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ CTA SECTION ═══════════════════════════════════════════════════════ */}
      <section className="py-12 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="bg-primary rounded-[3rem] p-8 lg:py-10 lg:px-12 relative overflow-hidden text-center lg:text-left">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="text-blue-100/60 text-tiny font-black uppercase tracking-[0.4em] mb-2 block">Sustainable Growth</span>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
                Precision Hiring for <span className="text-secondary">Long-Term Success</span>
              </h2>
              <p className="text-blue-100/80 text-lg mb-6 max-w-2xl font-medium">
                Cultivate stability and explosive growth by partnering with a dedicated talent architect. Get absolute agility and uncompromised quality delivered directly to your projects.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <a
                  href="tel:+16307991556"
                  className="px-10 py-5 bg-white text-primary font-black rounded-xl hover:bg-blue-50 transition-all flex items-center gap-3 uppercase tracking-widest text-subtext shadow-2xl active:scale-95"
                >
                  630-799-1556
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="grid grid-cols-1 gap-3">
                {[
                  { k: 'Longevity', v: '94% Retention Mark' },
                  { k: 'Quality', v: 'Top 1% Global Access' },
                  { k: 'Velocity', v: 'Accelerated Time-to-Impact' }
                ].map((item) => (
                  <div key={item.k} className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl text-left">
                    <p className="text-blue-100/50 text-[10px] font-black uppercase tracking-widest">{item.k}</p>
                    <p className="text-white text-small font-bold mt-1">{item.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default SolutionFulltime;
